import { memo } from "react";
import { Link } from "react-router-dom";
import { Sparkles, MapPin, CupSoda, BadgePercent } from "lucide-react";
import { LOGO_URL } from "@/components/BobaLogo";
import HeroPearls from "@/components/home/HeroPearls";
import { usePublicSettings } from "@/lib/queries";

function Hero() {
  // Same flag the nav uses — when ordering is paused we swap the primary
  // hero CTA from "Order online" to "Find us here" so visitors land on
  // the pop-up calendar instead of a broken cart flow.
  const { data: pubSettings } = usePublicSettings();
  const orderingEnabled = pubSettings?.online_ordering_enabled !== false;
  return (
    <section
      data-testid="home-hero"
      className="relative overflow-hidden"
      style={{
        background:
          "radial-gradient(1100px circle at 12% 10%, hsla(252,90%,80%,0.55), transparent 55%)," +
          "radial-gradient(900px circle at 88% 25%, hsla(330,90%,80%,0.50), transparent 55%)," +
          "linear-gradient(180deg, hsl(260 70% 98%), hsl(330 55% 98%))",
      }}
    >
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 pt-10 pb-14 md:pt-20 md:pb-24">
        <div className="grid gap-10 lg:gap-12 lg:grid-cols-12 items-center">
          {/* Copy */}
          <div className="lg:col-span-5 bp-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/85 border border-primary/15 text-xs font-medium text-primary shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-accent" /> Fresh brewed · Hand-stirred · Idaho-made
            </span>
            <h1 className="font-brand font-semibold text-[2.5rem] leading-[1.02] sm:text-5xl lg:text-6xl tracking-tight mt-5 text-foreground">
              Chewy pearls.<br />
              Unreal flavors.<br />
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Your boba place.
              </span>
            </h1>
            <p className="mt-5 text-muted-foreground max-w-xl text-[15px] sm:text-base leading-relaxed">
              Small-batch boba and tea drinks from Hailey, Ketchum and Sun Valley.
              Tap below to explore today's menu and join rewards.
            </p>
            <div className="mt-7 flex flex-wrap gap-2.5 sm:gap-3">
              {orderingEnabled ? (
                <Link
                  to="/order"
                  data-testid="home-hero-order-button"
                  className="bp-ripple inline-flex items-center gap-2 py-3 px-5 text-[15px] rounded-2xl font-bold text-white shadow-md hover:shadow-lg transition"
                  style={{ background: "linear-gradient(135deg, hsl(252 63% 54%), hsl(330 78% 60%))" }}
                >
                  <Sparkles className="w-4 h-4" /> Order online
                </Link>
              ) : (
                <Link
                  to="/events"
                  data-testid="home-hero-find-us-button"
                  className="bp-ripple inline-flex items-center gap-2 py-3 px-5 text-[15px] rounded-2xl font-bold text-white shadow-md hover:shadow-lg transition"
                  style={{ background: "linear-gradient(135deg, hsl(252 63% 54%), hsl(330 78% 60%))" }}
                >
                  <MapPin className="w-4 h-4" /> Find us here
                </Link>
              )}
              <Link
                to="/menu"
                data-testid="home-hero-view-menu-button"
                className="bp-btn-primary bp-ripple inline-flex items-center gap-2 py-3 px-5 text-[15px]"
              >
                <CupSoda className="w-4 h-4" /> View Menu
              </Link>
              <Link
                to="/rewards"
                data-testid="home-hero-join-rewards-button"
                className="bp-btn-accent inline-flex items-center gap-2 py-3 px-5 text-[15px]"
              >
                <BadgePercent className="w-4 h-4" /> Join Rewards
              </Link>
              <Link
                to="/events"
                data-testid="home-hero-find-us-link"
                className="inline-flex items-center gap-2 rounded-xl border border-primary/20 bg-white/70 hover:bg-white px-4 py-2.5 text-sm font-medium text-primary transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <MapPin className="w-3.5 h-3.5" /> Visit us
              </Link>
            </div>

            <div className="mt-7 flex flex-wrap gap-2">
              {["Hand-cooked pearls", "Made fresh daily", "Hailey + Sun Valley"].map((t) => (
                <span key={t} className="bp-chip bg-white/80 border-primary/15 text-primary/80">
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Logo canvas */}
          <div className="lg:col-span-7 order-first lg:order-none">
            <div
              className="relative bp-sheen bp-noise rounded-[28px] sm:rounded-[32px] border border-white/60 overflow-hidden"
              style={{
                boxShadow:
                  "0 40px 120px -70px rgba(236,72,153,0.55), 0 30px 80px -50px rgba(91,63,209,0.35)",
                background:
                  "radial-gradient(120% 120% at 20% 10%, #fff 0%, #fde8f3 55%, #e7dcff 100%)",
                minHeight: "340px",
              }}
            >
              <HeroPearls />

              <div
                aria-hidden="true"
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] rounded-full blur-3xl opacity-80"
                style={{
                  background:
                    "radial-gradient(circle at 30% 30%, hsla(330,78%,70%,0.55) 0%, hsla(252,63%,60%,0.35) 45%, transparent 70%)",
                }}
              />

              <div className="relative flex items-center justify-center p-10 sm:p-14 lg:p-16">
                <img
                  src={LOGO_URL}
                  alt="The Boba Place logo"
                  data-testid="home-hero-logo-image"
                  className="relative w-56 sm:w-64 lg:w-[360px] h-auto drop-shadow-[0_20px_36px_rgba(91,63,209,0.45)]"
                  draggable="false"
                  width="360"
                  height="360"
                  loading="eager"
                  fetchPriority="high"
                />
              </div>

              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-[2px]"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, hsla(252,63%,54%,0.45), hsla(330,78%,60%,0.55), transparent)",
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="bp-divider-splash" />
    </section>
  );
}

export default memo(Hero);
